import React, { FunctionComponent, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";

import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { Button } from "primereact/button";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { ITableConfigProp } from "../common/api/interfaces/interfaces";
import DatasetConfigEditor from "./dataset-config-editor/DatasetConfigEditor";


const defaultDataInputOutputTableConfig: ITableConfigProp = {
  config: {
    alignStartTo: "HOURS",
    timeOffset: 0,
    timeOffsetUnits: "HOURS",
    dataFrequency: "HOURS",
    updateFrequency: 5,
    maxHeight: 450,
  },
  datasets: [],
};

type DataInputOutputTableEditorProps = {
  config?: ITableConfigProp;
  onConfigChange: (config: ITableConfigProp) => void;
};

function DataInputOutputTableEditor({ config, onConfigChange }: DataInputOutputTableEditorProps) {
  let conf: ITableConfigProp = config ?? defaultDataInputOutputTableConfig;

  if (Object.keys(conf).length === 0) {
    conf = defaultDataInputOutputTableConfig;
  }

  const [currentConfig, setCurrentConfig] = useState<ITableConfigProp>(conf);
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>();
  const [datasetConfigEditorIsVisible, setDatasetConfigEditorVisibility] = useState(false);

  if (conf.datasets && conf.datasets.length > 0 && !selectedDatasets) {
    const datasets = conf.datasets.map((dataset: any) => {
      return dataset.identifier;
    });
    setSelectedDatasets(datasets);
  }

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const handleDatasetChange = (e: any) => {
    setSelectedDatasets(e);

    const selectedDatasetsArray = e.map((identifier: string) => {
      const oldDataset = currentConfig.datasets.find(dataset => {
        return dataset.identifier === identifier;
      });
      if (oldDataset) return oldDataset;
      return { identifier };
    });

    setCurrentConfig({
      ...currentConfig,
      datasets: selectedDatasetsArray,
    });
  };

  const handleDatasetConfigEditClick = () => {
    setDatasetConfigEditorVisibility(true);
  };

  const handleConfigChange = (e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
    setCurrentConfig({
      ...currentConfig,
      config: {
        ...currentConfig.config,
        [e.target.id]: e.target.value?.toString().toUpperCase(),
      },
    });
  };

  const handleInputSwitchChange = (e: InputSwitchChangeEvent) => {
    setCurrentConfig({
      ...currentConfig,
      config: {
        ...currentConfig.config,
        [e.target.id]: e.target.value,
      },
    });
  };

  const handleInputTextChange = (e: any) => {
    setCurrentConfig({
      ...currentConfig,
      config: {
        ...currentConfig.config,
        [e.target.id]: e.target.value,
      },
    });
  };

  return (
    <div>
      <div className="grid p-fluid">
        <div className="col-12 flex gap-2 flex-column mb-3">
          <label htmlFor="datasets">{T("word.datasets")}</label>
          <div className="flex">
            <DatasetMultiSelect
              id="datasets"
              value={selectedDatasets}
              onChange={handleDatasetChange}
            />
            <Button
              icon="pi pi-pencil"
              type="button"
              className="p-button-secondary"
              style={{ marginLeft: "10px" }}
              tooltip={T("action.edit-dataset-config")}
              onClick={() => handleDatasetConfigEditClick()}
            />
            <DatasetConfigEditor
              currentConfig={currentConfig}
              setCurrentConfig={setCurrentConfig}
              onConfigChange={onConfigChange}
              datasetConfigEditorIsVisible={datasetConfigEditorIsVisible}
              setDatasetConfigEditorVisibility={setDatasetConfigEditorVisibility}
              setSelectedDatasets={setSelectedDatasets}
            />
          </div>
        </div>
        <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="timeOffset">{T("load-conf.time-offset")}</label>
          <div className="p-inputgroup">
            <InputNumber
              id="timeOffset"
              value={currentConfig.config?.timeOffset as number}
              onValueChange={handleConfigChange}
            />
            <TimeUnitDropdown
              id="timeOffsetUnits"
              className="p-inputgroup-addon p-0"
              value={
                currentConfig.config?.timeOffsetUnits ? currentConfig.config.timeOffsetUnits : ""
              }
              onChange={handleConfigChange}
            />
          </div>
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="dataFrequency">{T("config.data-frequency")}</label>
          <TimeUnitDropdown
            id="dataFrequency"
            className="p-0"
            value={currentConfig.config?.dataFrequency ? currentConfig.config.dataFrequency : ""}
            onChange={handleConfigChange}
          />
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="alignStartTo">{T("load-conf.align-start-to")}</label>
          <TimeUnitDropdown
            id="alignStartTo"
            className="p-0"
            value={currentConfig.config?.alignStartTo ? currentConfig.config.alignStartTo : ""}
            onChange={handleConfigChange}
          />
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="updateFrequency"
              value={currentConfig.config?.updateFrequency as number}
              onValueChange={handleConfigChange}
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="maxHeight">{T("config.max-height")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="maxHeight"
              value={currentConfig.config?.maxHeight as number}
              onValueChange={handleConfigChange}
            />
            <span className="p-inputgroup-addon">px</span>
          </div>
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="displayNote">{T("action.add-note")}</label>
          <InputSwitch
            id="displayNote"
            checked={Boolean(currentConfig.config?.displayNote)}
            onChange={handleInputSwitchChange}
          />
        </div>
        {currentConfig.config?.displayNote && (
          <div className="col-12">
            <label htmlFor="note">{T("word.note")}</label>
            <InputText
              id="note"
              value={currentConfig.config?.note}
              onChange={handleInputTextChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DataInputOutputTableEditor as FunctionComponent;